import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Shield, Calendar, Clock, Edit2, Save, X, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminSchemeSettings() {
  const [scheme, setScheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});

  const fetchScheme = useCallback(async () => {
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const res = await fetch(`${API}/api/admin/scheme-config`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setScheme(data);
      setDraft({ start_date: data.start_date, end_date: data.end_date, status: data.status });
    } catch { toast.error('Failed to load scheme config'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchScheme(); }, [fetchScheme]);

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const res = await fetch(`${API}/api/admin/scheme-config`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      if (res.ok) {
        toast.success('Scheme config updated');
        setEditing(false);
        fetchScheme();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update');
      }
    } catch { toast.error('Failed to update scheme config'); }
  };

  const isActive = scheme?.status === 'active';
  const today = new Date().toISOString().split('T')[0];
  const schemeStarted = scheme && today >= scheme.start_date;
  const schemeEnded = scheme && today > scheme.end_date;
  const daysLeft = scheme ? Math.max(0, Math.ceil((new Date(scheme.end_date) - new Date()) / (1000 * 60 * 60 * 24))) : 0;

  if (loading) return <div className="flex items-center justify-center py-20 text-sm text-slate-400">Loading scheme settings...</div>;
  if (!scheme) return <div className="p-6 text-sm text-slate-400">No scheme config found.</div>;

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6" data-testid="admin-scheme-settings">
      <div>
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#1e3a5f]" /> Scheme Settings
        </h1>
        <p className="text-sm text-slate-500 mt-1">Configure the active regulatory scheme. Dates and status control regime determination for all new leads.</p>
      </div>

      {/* Status Banner */}
      <div className={`rounded-lg border p-4 flex items-center gap-3 ${
        schemeEnded ? 'bg-red-50 border-red-200' :
        isActive && schemeStarted ? 'bg-emerald-50 border-emerald-200' :
        isActive ? 'bg-blue-50 border-blue-200' :
        'bg-slate-50 border-slate-200'
      }`}>
        {schemeEnded ? (
          <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
        ) : isActive ? (
          <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
        ) : (
          <Info className="w-5 h-5 text-slate-400 shrink-0" />
        )}
        <div>
          <p className={`text-sm font-semibold ${schemeEnded ? 'text-red-700' : isActive ? 'text-emerald-700' : 'text-slate-700'}`}>
            {schemeEnded ? 'Scheme Window Ended' :
             isActive && schemeStarted ? `Scheme Active — ${daysLeft} days remaining` :
             isActive ? `Scheme Upcoming — Starts ${scheme.start_date}` :
             'Scheme Inactive'}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            {scheme.scheme_name} · {scheme.circular}
          </p>
        </div>
      </div>

      {/* Config Card */}
      <Card>
        <CardContent className="p-5 space-y-5">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-800">{scheme.full_name}</p>
            {!editing ? (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="text-xs gap-1.5" data-testid="edit-scheme-btn">
                <Edit2 className="w-3 h-3" /> Edit
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setEditing(false); setDraft({ start_date: scheme.start_date, end_date: scheme.end_date, status: scheme.status }); }} className="text-xs gap-1.5">
                  <X className="w-3 h-3" /> Cancel
                </Button>
                <Button size="sm" onClick={handleSave} className="text-xs gap-1.5 bg-[#1e3a5f] hover:bg-[#2a4f7a]" data-testid="save-scheme-btn">
                  <Save className="w-3 h-3" /> Save Changes
                </Button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold flex items-center gap-1 mb-1.5">
                <Calendar className="w-3 h-3" /> Start Date
              </label>
              {editing ? (
                <Input type="date" value={draft.start_date} onChange={e => setDraft(d => ({ ...d, start_date: e.target.value }))} className="h-9 text-sm" data-testid="scheme-start-date" />
              ) : (
                <p className="text-sm font-semibold text-slate-800 bg-slate-50 rounded px-3 py-2">{scheme.start_date}</p>
              )}
            </div>
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold flex items-center gap-1 mb-1.5">
                <Calendar className="w-3 h-3" /> End Date
              </label>
              {editing ? (
                <Input type="date" value={draft.end_date} onChange={e => setDraft(d => ({ ...d, end_date: e.target.value }))} className="h-9 text-sm" data-testid="scheme-end-date" />
              ) : (
                <p className="text-sm font-semibold text-slate-800 bg-slate-50 rounded px-3 py-2">{scheme.end_date}</p>
              )}
              <p className="text-[10px] text-slate-400 mt-1">Update when MCA extends deadline</p>
            </div>
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold flex items-center gap-1 mb-1.5">
                <Clock className="w-3 h-3" /> Status
              </label>
              {editing ? (
                <select value={draft.status} onChange={e => setDraft(d => ({ ...d, status: e.target.value }))} className="w-full h-9 text-sm border rounded px-3 bg-white" data-testid="scheme-status-select">
                  <option value="active">Active</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="expired">Expired</option>
                </select>
              ) : (
                <p className={`text-sm font-semibold rounded px-3 py-2 ${
                  scheme.status === 'active' ? 'bg-emerald-50 text-emerald-700' :
                  scheme.status === 'expired' ? 'bg-red-50 text-red-700' :
                  'bg-blue-50 text-blue-700'
                }`}>{scheme.status.charAt(0).toUpperCase() + scheme.status.slice(1)}</p>
              )}
            </div>
          </div>

          {/* Fee Multipliers */}
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-2">Fee Multipliers (under scheme)</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-3">
                <p className="text-[9px] text-slate-400 uppercase tracking-wide">Annual Filing Additional Fee</p>
                <p className="text-lg font-bold text-blue-700 mt-1">{(scheme.fee_multipliers.annual_filing_additional * 100).toFixed(0)}%</p>
                <p className="text-[10px] text-slate-500">of normal additional fees</p>
              </div>
              <div className="bg-red-50/50 border border-red-100 rounded-lg p-3">
                <p className="text-[9px] text-slate-400 uppercase tracking-wide">Strike-off Filing Fee</p>
                <p className="text-lg font-bold text-red-700 mt-1">{(scheme.fee_multipliers.strike_off_filing * 100).toFixed(0)}%</p>
                <p className="text-[10px] text-slate-500">of normal filing fees</p>
              </div>
              <div className="bg-amber-50/50 border border-amber-100 rounded-lg p-3">
                <p className="text-[9px] text-slate-400 uppercase tracking-wide">Dormancy Filing Fee</p>
                <p className="text-lg font-bold text-amber-700 mt-1">{(scheme.fee_multipliers.dormancy_filing * 100).toFixed(0)}%</p>
                <p className="text-[10px] text-slate-500">of normal filing fees</p>
              </div>
            </div>
          </div>

          {/* Exclusions */}
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-2">Exclusions (Not eligible under scheme)</p>
            <div className="flex flex-wrap gap-2">
              {scheme.exclusions?.map((ex, i) => (
                <span key={i} className="text-[10px] px-2.5 py-1 rounded-full bg-red-50 text-red-600 border border-red-200 font-medium">
                  {ex.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>

          {/* Immunity Rules */}
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-2">Immunity Rules</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <p className="text-[10px] font-semibold text-slate-500 mb-1">Sections 92 & 137 (Annual Filings)</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-[11px]">
                    <CheckCircle className="w-3 h-3 text-emerald-500" />
                    <span className="text-slate-700">Immunity if filed before adjudication notice</span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px]">
                    <CheckCircle className="w-3 h-3 text-emerald-500" />
                    <span className="text-slate-700">Immunity if filed within 30 days of notice</span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px]">
                    <X className="w-3 h-3 text-red-400" />
                    <span className="text-slate-400">No immunity after 30-day window</span>
                  </div>
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <p className="text-[10px] font-semibold text-slate-500 mb-1">Legacy Forms (Old Act)</p>
                <div className="flex items-center gap-2 text-[11px]">
                  <CheckCircle className="w-3 h-3 text-emerald-500" />
                  <span className="text-slate-700">Immunity if no prosecution filed as of filing date</span>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {scheme.notes && (
            <div className="bg-amber-50/50 rounded-lg p-3 border border-amber-100">
              <p className="text-[10px] font-semibold text-amber-600 mb-1">Notes</p>
              <p className="text-xs text-slate-600">{scheme.notes}</p>
            </div>
          )}

          {scheme.updated_at && (
            <p className="text-[10px] text-slate-400">Last updated: {new Date(scheme.updated_at).toLocaleString('en-IN')} by {scheme.updated_by}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
